import {
  Button,
  Checkbox,
  Descriptions,
  Divider,
  Popover,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd"
import React, { useEffect, useState } from "react"
import { collection, onSnapshot, orderBy, query, where, limit } from "firebase/firestore"
import { db } from "../../../firebase/config"
import moment from "moment"
import { OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
import {
  CloseOutlined,
  FilterFilled,
  FilterOutlined,
  LikeFilled,
  PlayCircleFilled,
} from "@ant-design/icons"
import ViewDoneListTechnical from "./ViewDoneListTechnical"
import EditDoneOrder from "./EditDoneOrder"

const { Text, Link, Paragraph } = Typography

export default function DoneList({ doneForMachine = "all" }) {
  const [vivaPrintData, setVivaPrintData] = useState([])
  const [shortColumn, setShortColumn] = useState(true)
  // Object to manage visibility of each column
  const [visibleColumns, setVisibleColumns] = useState({
    orderNumber: true,
    created: true,
    client: true,
    product: true,
    quantity: true,
    dieCut: true,
    material: true,
    height: true,
    width: true,
    plate: true,
    technical: true,
    doneByRewinder: true,
    editDone: true,
  })
  const [filterActive, setFilterActive] = useState(false)
  // loading for while data is sorting/fetching
  const [isSorting, setIsSorting] = useState(true)
  // Limit state for pagination (initially 300)
  const [limitCount, setLimitCount] = useState(300)

  // client filter
  let clientFIlters =
    vivaPrintData &&
    vivaPrintData.reduce((accumulator, item) => {
      const clientName = item.client.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)
      if (existingItem) {
        existingItem.value = clientName
      } else {
        accumulator.push({
          text: clientName,
          value: clientName,
        })
      }
      return accumulator
    }, [])

  let productFilters =
    vivaPrintData &&
    vivaPrintData.reduce((accumulator, item) => {
      const productName = item.selectedProduct.product.productName
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)
      if (existingItem) {
        existingItem.value = productName
      } else {
        accumulator.push({
          text: productName,
          value: productName,
        })
      }
      return accumulator
    }, [])

  let materialFilter =
    vivaPrintData &&
    vivaPrintData.reduce((accumulator, item) => {
      const materialName = item.selectedProduct.material
      const existingItem = accumulator.find((filterItem) => filterItem.text === materialName)
      if (existingItem) {
        existingItem.value = materialName
      } else {
        accumulator.push({
          text: materialName,
          value: materialName,
        })
      }
      return accumulator
    }, [])

  const vivaPrintingCols = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "center",
      filterTitle: "Order Number",
      ellipsis: shortColumn ? { tooltip: true } : false,
      key: "orderNumber",
      sorter: (a, b) => a.justOrderNumber - b.justOrderNumber,
      render: (value, record) => OrderAndPrefix(record),
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "center",
      key: "client",
      filterTitle: "Clients",
      filters: clientFIlters,
      onFilter: (value, record) => record.client.client.startsWith(value),
      filterSearch: true,
      sorter: (a, b) => {
        const clientA = a.client?.client || ""
        const clientB = b.client?.client || ""
        return clientA.localeCompare(clientB)
      },
      ellipsis: shortColumn ? { tooltip: true } : false,
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },

    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "center",
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct.product.productName === value,
      filterSearch: true,
      filterTitle: "Products",
      key: "product",
      width: "15%",
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amountCreated",
      align: "center",
      filterTitle: "Created Amount",
      key: "quantity",
      width: "6%",
      sorter: (a, b) => a.amountCreated - b.amountCreated,
      render: (value, record) => {
        if (record.selectedProduct.machine === "Tape") {
          return (
            <>
              <Text copyable={{ text: record.rollCount }}>{record.rollCount} Rolls</Text>
            </>
          )
        } else {
          return (
            <>
              <Popover
                content={
                  <>
                    <div style={{ padding: 5 }}>
                      <Descriptions column={1} style={{ width: 150 }}>
                        <Descriptions.Item label="Ordered">{record.labelCount}</Descriptions.Item>
                        <Descriptions.Item label="Created">{value}</Descriptions.Item>
                      </Descriptions>
                    </div>
                  </>
                }
                title="Amount details"
                trigger="click">
                <Link copyable={{ text: value }}>{value}</Link>
              </Popover>
            </>
          )
        }
      },
    },
    {
      title: "Die-cut",
      dataIndex: ["selectedProduct", "knifeCode"],
      align: "center",
      filterTitle: "Die Cut's",
      key: "dieCut",
      width: "8%",
      hidden: doneForMachine === "Tape",
    },
    {
      title: "Width",
      dataIndex: ["selectedProduct", "labelWidth"],
      align: "center",
      filterTitle: "Label's Width",
      width: "5%",
      key: "width",
      hidden: doneForMachine === "Tape",
    },
    {
      title: "Height",
      dataIndex: ["selectedProduct", "labelHeight"],
      align: "center",
      width: "5%",
      filterTitle: "Label's Height",
      key: "height",
      hidden: doneForMachine === "Tape",
    },
    {
      title: "Material",
      dataIndex: ["selectedProduct", "material"],
      align: "center",
      filterTitle: "Materials",
      key: "material",
      width: "5%",
      ellipsis: shortColumn ? { tooltip: true } : false,
      filters: materialFilter,
      onFilter: (value, record) => record.selectedProduct.material.startsWith(value),
      filterSearch: true,
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Plate",
      dataIndex: ["selectedPlate", "height"],
      align: "center",
      filterTitle: "Plates",
      width: "5%",
      key: "plate",
      hidden: doneForMachine === "Tape",
      ellipsis: {
        showTitle: false,
      },
      render: (item) => {
        if (item) {
          return (
            <Tooltip placement="topLeft" title={item}>
              {item}
            </Tooltip>
          )
        } else {
          return <CloseOutlined style={{ color: "#f5222d" }} />
        }
      },
    },
    {
      title: "Technical",
      dataIndex: ["selectedProduct", "technicalPage"],
      key: "technical",
      filterTitle: "Technical Pages",
      align: "center",
      width: "6%",
      render: (value, record) => {
        if (value) {
          return (
            <>
              <ViewDoneListTechnical link={value} />
            </>
          )
        } else {
          return <CloseOutlined style={{ color: "#f5222d" }} />
        }
      },
    },
    {
      title: "Started",
      align: "center",
      dataIndex: "created",
      key: "created",
      filterTitle: "Creation Date",
      sorter: (a, b) => {
        if (a.created && b.created) {
          return a.created.seconds - b.created.seconds
        } else if (a.created && !b.created) {
          return -1
        } else if (!a.created && b.created) {
          return 1
        } else {
          return 0
        }
      },
      render: (value, record) => {
        if (record.created) {
          const popoverContent = (
            <div>
              <p>Date: {moment.unix(value.seconds).format("DD.MM.YYYY")}</p>
              <p>Time: {moment.unix(value.seconds).format("HH:mm:ss")}</p>
            </div>
          )

          return (
            <Popover content={popoverContent}>
              <Tag icon={<PlayCircleFilled />} color="#2f54eb">
                {moment.unix(value.seconds).format("DD.MM.YYYY")}
              </Tag>
            </Popover>
          )
        } else if (record.machine === "Tape") {
          return <Text>no date</Text>
        } else {
          return <Text>No data</Text>
        }
      },
    },
    {
      title: "Ended",
      align: "center",
      dataIndex: "doneByRewinder",
      key: "doneByRewinder",
      hidden: doneForMachine === "Tape",
      filterTitle: "Done date",
      sorter: (a, b) => {
        if (a.doneByRewinder && b.doneByRewinder) {
          return a.doneByRewinder.seconds - b.doneByRewinder.seconds
        } else if (a.doneByRewinder && !b.doneByRewinder) {
          return -1
        } else if (!a.doneByRewinder && b.doneByRewinder) {
          return 1
        } else {
          return 0
        }
      },
      render: (value, record) => {
        if (record.doneByRewinder) {
          const popoverContent = (
            <div>
              <p>Date: {moment.unix(value.seconds).format("DD.MM.YYYY")}</p>
              <p>Time: {moment.unix(value.seconds).format("HH:mm:ss")}</p>
            </div>
          )

          return (
            <Popover content={popoverContent}>
              <Tag icon={<LikeFilled />} color="#52c41a">
                {moment.unix(value.seconds).format("DD.MM.YYYY")}
              </Tag>
            </Popover>
          )
        } else if (record.machine === "Tape") {
          return <Text>no date</Text>
        } else {
          return <Text>No data</Text>
        }
      },
    },

    {
      title: "Edit",
      align: "center",
      key: "editDone",
      alwaysVisible: doneForMachine === "all" ? true : false,
      hidden: doneForMachine === "all" ? true : false,
      width: "5%",
      filterTitle: "Edit Done List",
      render: (record) => <EditDoneOrder record={record} />,
    },
  ]

  // Get data using onSnapshot with a dynamic query.
  useEffect(() => {
    setIsSorting(true)
    const ref = collection(db, "orders")
    const conditions = []
    let orderByFields = []

    if (doneForMachine === "Tape") {
      // For Tape: only fetch orders with orderStatus == 6 and machine equals "Tape"
      conditions.push(where("orderStatus", "==", 6))
      conditions.push(where("selectedProduct.machine", "==", "Tape"))
      orderByFields = [orderBy("doneByTape", "desc")]
    } else if (doneForMachine === "Viva" || doneForMachine === "Arsoma") {
      // For Viva or Arsoma, fetch orders where orderStatus is in [5, 6, 7]
      conditions.push(where("orderStatus", "in", [5, 6, 7]))
      orderByFields = [orderBy("doneByRewinder", "desc")]
    } else if (doneForMachine === "all") {
      // For "all", exclude Tape orders and order by selectedProduct.machine and then doneByRewinder
      conditions.push(where("orderStatus", "==", 6))
      conditions.push(where("selectedProduct.machine", "!=", "Tape"))
      orderByFields = [orderBy("selectedProduct.machine"), orderBy("doneByRewinder", "desc")]
    } else {
      // For other specific machine values
      conditions.push(where("orderStatus", "==", 6))
      conditions.push(where("selectedProduct.machine", "==", doneForMachine))
      orderByFields = [orderBy("doneByRewinder", "desc")]
    }

    const q = query(ref, ...conditions, ...orderByFields, limit(limitCount))

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        let results = []
        snapshot.docs.forEach((doc) => {
          results.push({ ...doc.data(), key: doc.id, id: doc.id })
        })

        // Filter out orders with the special uid on the frontend.
        const filteredResults = results.filter(
          (order) => order.uid !== "E1XNaWvT6PccXRSKB3JNlNehI362"
        )

        // Optional additional sorting (if needed)
        filteredResults.sort((a, b) => {
          const getTime = (record) => {
            if (record.doneByRewinder) {
              if (typeof record.doneByRewinder.toDate === "function") {
                return record.doneByRewinder.toDate().getTime()
              }
              return new Date(record.doneByRewinder).getTime()
            }
            return 0
          }
          return getTime(b) - getTime(a)
        })

        setVivaPrintData(filteredResults)
        setIsSorting(false)
      },
      (error) => {
        console.error("Error fetching orders:", error)
        setIsSorting(false)
      }
    )

    return () => unsubscribe()
  }, [doneForMachine, limitCount])

  // handles the action for visibility change
  const handleVisibilityChange = (key, checked) => {
    setVisibleColumns((prev) => {
      const updatedVisibleColumns = { ...prev, [key]: checked }
      const isAnyColumnHidden = Object.values(updatedVisibleColumns).includes(false)
      setFilterActive(isAnyColumnHidden)
      return updatedVisibleColumns
    })
  }

  // controls for handling the filter
  const columnVisibilityControls = (
    <div style={{ margin: -8, background: "white", borderRadius: "5px" }}>
      <Space direction="vertical" style={{ padding: 15 }}>
        {vivaPrintingCols
          .filter((col) => !col.alwaysVisible)
          .map((col) => (
            <Checkbox
              key={col.key}
              checked={visibleColumns[col.key]}
              onChange={(e) => handleVisibilityChange(col.key, e.target.checked)}>
              {col.filterTitle}
            </Checkbox>
          ))}
      </Space>
    </div>
  )

  // This filters out columns that should NOT be visible
  const filteredColumns = vivaPrintingCols.filter((column) => visibleColumns[column.key])

  return (
    <>
      <Paragraph italic style={{ color: "#8c8c8c" }}>
        Done list represents all the orders that are in "Warehouse" or "Winder"
      </Paragraph>
      <Space split={<Divider type="vertical" />}>
        <Popover
          color="#597ef7"
          placement="bottomLeft"
          trigger="click"
          content={columnVisibilityControls}>
          <Button
            type={filterActive ? "primary" : "dashed"}
            icon={filterActive ? <FilterFilled /> : <FilterOutlined />}>
            <Space>
              Filter Columns
              {filterActive ? (
                <Text style={{ color: "lightgray" }} italic>
                  (active)
                </Text>
              ) : (
                ""
              )}
            </Space>
          </Button>
        </Popover>
        <Space>
          <Text>Shorten columns?</Text>
          <Checkbox checked={shortColumn} onChange={(e) => setShortColumn(e.target.checked)} />
        </Space>
        <Button
          size="small"
          type="primary"
          onClick={() => setLimitCount(limitCount + 300)}
          disabled={isSorting}>
          Load older orders
        </Button>
      </Space>
      <Table
        style={{ marginTop: 5 }}
        rowKey={(record) => record.id}
        size="small"
        pagination={{ pageSize: 40 }}
        dataSource={vivaPrintData}
        loading={isSorting}
        columns={filteredColumns}
      />
    </>
  )
}
